import { inventoryApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  InventoryI,
  GetInventoryParamsI,
  InventoryMovementI,
  GetInventoryMovementsParamsI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getInventory = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  clientId,
}: GetInventoryParamsI): Promise<ServiceResponseI<DataI<InventoryI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId.toString());
    if (clientId) params.append("clientId", clientId.toString());

    const { data } = await inventoryApi.get<
      ServiceResponseI<DataI<InventoryI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const inventoryMovementPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await inventoryApi.post<ServiceResponseI<string>>(
      "/movement",
      payload,
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getLowStock = async (
  branchId: number,
): Promise<ServiceResponseI<InventoryI[]>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await inventoryApi.get<ServiceResponseI<InventoryI[]>>(
      "/low-stock",
      {
        params: { branchId },
        headers: { Authorization: validateToken },
      },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useInventory = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  clientId,
}: GetInventoryParamsI) => {
  const inventoryQuery = useQuery(
    [
      "inventory",
      {
        pagination,
        sorting,
        globalFilter,
        branchId,
        clientId,
      },
    ],
    () =>
      getInventory({
        pagination,
        sorting,
        globalFilter,
        branchId,
        clientId,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { inventoryQuery };
};

// ─── GET /inventory/movements ─────────────────────────────────────────────────

const getInventoryMovements = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  productId,
  typeId,
  range,
  rangeVal,
}: GetInventoryMovementsParamsI): Promise<
  ServiceResponseI<DataI<InventoryMovementI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    rangeVal && params.append("range", JSON.stringify(range));
    if (branchId) params.append("branchId", branchId.toString());
    if (productId) params.append("productId", productId.toString());
    if (typeId) params.append("typeId", typeId.toString());

    const { data } = await inventoryApi.get<
      ServiceResponseI<DataI<InventoryMovementI>>
    >("/movements", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useInventoryMovements = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  productId,
  typeId,
  range,
  rangeVal,
}: GetInventoryMovementsParamsI) => {
  const movementsQuery = useQuery(
    [
      "inventory-movements",
      {
        pagination,
        sorting,
        globalFilter,
        branchId,
        productId,
        typeId,
        range,
        rangeVal,
      },
    ],
    () =>
      getInventoryMovements({
        pagination,
        sorting,
        globalFilter,
        branchId,
        productId,
        typeId,
        range,
        rangeVal,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { movementsQuery };
};
